import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export type StaffRole = "skyward_admin" | "skyward_tech";
export type Role = StaffRole | "org_owner" | "org_user" | "viewer";

export const ROLE_LABEL: Record<Role, string> = {
  skyward_admin: "Skyward admin",
  skyward_tech: "Skyward technician",
  org_owner: "Company owner",
  org_user: "User",
  viewer: "Viewer (read-only)",
};
export const STAFF_ROLES: Role[] = ["skyward_admin", "skyward_tech"];
export const CLIENT_ROLES: Role[] = ["org_owner", "org_user", "viewer"];

export type Org = {
  id: string; name: string; type: "internal" | "client"; plan: string | null;
  status: "active" | "suspended" | "closed"; contact_name: string | null; contact_email: string | null;
  contact_phone: string | null; address: string | null; notes: string | null; created_at: string;
  map_providers: string[]; reminder_emails: string[]; reminder_days: number[];
  plan_id?: string | null; billing_start?: string | null;
};
export type Group = { id: string; org_id: string; name: string };

export type AdminUser = {
  id: string; email: string; full_name: string | null; phone: string | null;
  org_id: string | null; org_name: string | null; role: Role | null; group_scope: string[];
  org_ids?: string[]; org_names?: string[];   // V3.10.2: every company the user belongs to
  client_org_ids?: string[]; client_org_names?: string[];   // V3.13.1: client companies a Super Admin also owns
  status: "active" | "invited" | "disabled"; must_change_password: boolean;
  last_sign_in_at: string | null; created_at: string; invited_at: string | null; is_me: boolean;
  access_email_at?: string | null; access_email_kind?: "invite" | "welcome" | "reset" | null;
  super_admin?: boolean; support_view?: boolean;
};
/** Hasn't got in yet: invite not accepted, or an account (e.g. temporary password) that has never signed in. */
export const notSignedIn = (u: AdminUser) => u.status !== "disabled" && !u.last_sign_in_at;
export type Sim = {
  id: string; msisdn: string | null; iccid: string | null; carrier: string | null; plan: string | null;
  status: "blank" | "requested" | "stock" | "active" | "suspended" | "retired"; notes: string | null;
  batch: string | null; received_at: string | null; requested_at: string | null; activated_at: string | null;
};
export type AdminDevice = {
  id: string; org_id: string; traccar_id: number | null; unique_id: string; name: string;
  model: string | null; protocol: string | null; icon: string | null; sim_id: string | null; group_id: string | null;
  status: "active" | "suspended" | "retired"; notes: string | null; created_at: string;
  device_state: { seen_at: string | null; fix_at: string | null; speed_kn?: number | null } | null;
};
export type Unregistered = {
  traccar: { traccar_id: number; unique_id: string; name: string; model: string | null; status: string | null; last_update: string | null }[];
  unmatched: { unique_id: string; traccar_id: number | null; hits: number; last_seen: string }[];
};
export type AuditRow = {
  id: number; at: string; actor_email: string | null; action: string; entity: string;
  entity_id: string | null; org_id: string | null; summary: string | null;
};

/** Call the admin API with the signed-in user's token. Throws Error(message) on failure. */
export async function adminApi<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const r = await fetch(`/api/admin/${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const res = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(res?.error ?? `Request failed (${r.status})`);
  return res as T;
}

/** The signed-in user's staff role, or null for client users. undefined while loading. */
export function useStaffRole(userId: string | undefined) {
  const [role, setRole] = useState<StaffRole | null | undefined>(undefined);
  useEffect(() => {
    if (!userId) return setRole(null);
    supabase.from("memberships").select("role").eq("user_id", userId).then(({ data }) => {
      const roles = (data ?? []).map((r) => r.role);
      setRole(roles.includes("skyward_admin") ? "skyward_admin" : roles.includes("skyward_tech") ? "skyward_tech" : null);
    });
  }, [userId]);
  return role;
}

/** Server connection details shown when setting up a tracker. */
export const SERVER_HOST = "gps.trackingfiji.com";
export const SERVER_IP = "13.211.231.143";
export const PROTOCOL_INFO: Record<string, { label: string; port: number | null; setup: (sim?: string) => string[] }> = {
  h02: {
    label: "Sinotrack ST-901 / H02", port: 5013,
    setup: () => [`8040000 ${SERVER_IP} 5013   (server + port)`, "8030000 live.vodafone.com.fj   (APN — Vodafone)", "8050000 30   (report every 30 s)", "RCONF   (check settings)"],
  },
  jt808: {
    label: "JT808 (Sinotrack ST-901A etc.)", port: 5015,
    setup: () => [`8040000 ${SERVER_IP} 5015   (server + port)`, "8050000 30   (report every 30 s)", "RCONF   (check settings)"],
  },
  teltonika: {
    label: "Teltonika (FMB/FMC/FM)", port: 5027,
    setup: () => [`Server: ${SERVER_HOST}  Port: 5027  Protocol: TCP`, "Set with Teltonika Configurator or SMS: setparam 2004:" + SERVER_HOST + ";2005:5027"],
  },
  gt06: { label: "GT06 / Concox", port: null, setup: () => ["Port not opened yet — ask Skyward to enable GT06 on the server"] },
  osmand: { label: "Phone app (OsmAnd / Traccar Client)", port: null, setup: () => ["Not enabled yet"] },
  other: { label: "Other", port: null, setup: () => ["Check the protocol with Skyward before installing"] },
};

// ---------------------------------------------------------------- V3.8.0 device commands
export type CommandParam = { key: string; label: string; kind: "number" | "text"; min?: number; max?: number; default?: string | number; unit?: string;
  /** V3.9.4: text must match (server-checked), with a hint shown when it doesn't */ pattern?: string; hint?: string };
export type CommandPreset = {
  id: string; label: string; description: string | null; protocol: string | null; model: string | null; channel: "data" | "sms";
  type: string; attributes: Record<string, unknown>; params: CommandParam[]; danger: boolean; verified: boolean; tech_allowed: boolean;
  active: boolean; sort: number; notes: string | null; updated_at: string;
};
export type DeviceCommand = {
  id: number; device_id: string; preset_id: string | null; label: string; channel: string; type: string; attributes: Record<string, unknown>;
  status: "sent" | "queued" | "replied" | "no_reply" | "failed"; error: string | null; result: string | null; reason: string | null;
  sent_by_email: string | null; created_at: string; result_at: string | null;
};
export type CommandInfo = { online: boolean; status: string; last_update?: string | null; types: string[]; note?: string };
const norm = (v: string | null | undefined) => (v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
/** Same rule as the server: a preset fits when its protocol and model (if set) match the device's. */
export const presetFits = (p: Pick<CommandPreset, "protocol" | "model">, d: { protocol: string | null; model: string | null }) =>
  (!p.protocol || norm(p.protocol) === norm(d.protocol)) && (!p.model || norm(p.model) === norm(d.model));

// ---------------------------------------------------------------- V3.9.0 super admins + support view
export type AdminMe = { role: StaffRole; email: string; super_admin: boolean; support_view: boolean };
let meCache: Promise<AdminMe | null> | null = null;
export function useAdminMe(enabled = true) {
  const [me, setMe] = useState<AdminMe | null>(null);
  useEffect(() => {
    if (!enabled) return;
    meCache ??= adminApi<AdminMe>("GET", "me").catch(() => { meCache = null; return null; });
    let live = true;
    meCache.then((m) => { if (live) setMe(m); });
    return () => { live = false; };
  }, [enabled]);
  return me;
}
